import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/applicationAdmin/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { useAuthorization } from '~/hooks/useAuthorization'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'

export const UsersDownloadPage = () => {
  const { t } = useTranslation()
  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs: PageContainerProps['breadcrumbs'] = React.useMemo(
    () => [createBreadcrumb('top', {}), createBreadcrumb('usersDownload', {})],
    [createBreadcrumb],
  )

  const getAuthorization = useAuthorization()
  const notify = useNotify()
  const logEvent = useLogEvent()

  const handleDownloadCsv = React.useCallback<
    NonNullable<StyledButtonProps['onClick']>
  >(async () => {
    try {
      const authorization = await getAuthorization()

      if (authorization === '') {
        notify({
          style: 'error',
          content: t('failed_to_retrieve_authentication_information'),
        })

        return
      }

      const baseUrl =
        process.env.GRAPHQL_URL?.replace('/graphql', '') ??
        'http://localhost:8080'
      const response = await fetch(`${baseUrl}/api/users/csv`, {
        method: 'GET',
        headers: {
          Authorization: authorization,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to download CSV: ${response.status}`)
      }

      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
      const filename = filenameMatch?.[1] ?? 'users.csv'

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      logEvent('Download user list')
    } catch {
      notify({
        style: 'error',
        content: t('csv_download_failed'),
      })
    }
  }, [getAuthorization, logEvent, notify, t])

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack align="end" spacing={2} verticalAppearance="stretch">
        <StyledButton icon="download" onClick={handleDownloadCsv}>
          {t('download_csv')}
        </StyledButton>
      </VStack>
    </PageContainer>
  )
}
